import { ChangeEvent, FunctionComponent, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Snackbar,
  Alert,
  CircularProgress,
} from '@mui/material';
import { AxiosError } from 'axios';
import Button from '../button';
import ClaimsTrackerClient from '@shared/clients/ClaimsTrackerClient';
import { AnalysisTransitionOption, StageIds } from '@src/types/claimsTracker';

import './index.scss';

const MAX_DETAIL_LENGTH = 150;

interface Props {
  claimUId: string;
  /** currentStep (nombre de la etapa) para el payload de PUT /status. */
  currentStage: string;
  /** currentStepStatus (nombre del subestado actual) para el payload. */
  currentStatus: string;
  /** Se invoca tras intentar el cambio (éxito o conflicto) para refrescar estado + historial. */
  onStatusChanged?: () => void;
}

/**
 * HU-06 (CLAIM-3725): botón fijo "Actualizar análisis" + modal "Confirmar
 * Subestado" para cambiar el subestado dentro de la etapa Análisis (Purdy CR).
 * Los destinos se cargan de forma lazy al abrir el modal desde el backend
 * (matriz SubStateFlow). El cambio reutiliza el PUT /status con NewStatus y no
 * recarga la página: el refresco lo coordina el padre vía onStatusChanged.
 */
const UpdateAnalysis: FunctionComponent<Props> = ({
  claimUId,
  currentStage,
  currentStatus,
  onStatusChanged,
}): JSX.Element => {
  const [open, setOpen] = useState<boolean>(false);
  const [loadingOptions, setLoadingOptions] = useState<boolean>(false);
  const [optionsError, setOptionsError] = useState<boolean>(false);
  const [transitions, setTransitions] = useState<AnalysisTransitionOption[]>(
    [],
  );
  const [selectedStatusId, setSelectedStatusId] = useState<number | ''>('');
  const [detail, setDetail] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({ open: false, message: '', severity: 'success' });
  // Descarta respuestas de cargas obsoletas del GET de transiciones si el
  // usuario abre/cierra/reabre el modal rápido (evita pisar la lista vigente).
  const loadGenerationRef = useRef<number>(0);

  const resetModalState = () => {
    setTransitions([]);
    setSelectedStatusId('');
    setDetail('');
    setOptionsError(false);
  };

  // Lazy: los destinos permitidos se piden solo al abrir el modal, no en el
  // render de la pantalla.
  const handleOpen = async () => {
    resetModalState();
    setOpen(true);
    setLoadingOptions(true);
    const generation = ++loadGenerationRef.current;
    try {
      const result = await new ClaimsTrackerClient().getAnalysisTransitions(
        claimUId,
      );
      // Ignora la respuesta si llegó una carga posterior (reapertura rápida).
      if (generation !== loadGenerationRef.current) {
        return;
      }
      setTransitions(result?.transitions ?? []);
    } catch (error) {
      if (generation !== loadGenerationRef.current) {
        return;
      }
      console.error('Error loading analysis transitions:', error);
      setOptionsError(true);
      setTransitions([]);
    } finally {
      if (generation === loadGenerationRef.current) {
        setLoadingOptions(false);
      }
    }
  };

  const handleClose = () => {
    if (saving) {
      return;
    }
    setOpen(false);
  };

  const handleDetailChange = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    // Recorta a 150 aunque el usuario pegue un texto más largo.
    setDetail(event.target.value.slice(0, MAX_DETAIL_LENGTH));
  };

  const isConfirmDisabled = saving || loadingOptions || selectedStatusId === '';

  const handleConfirm = async () => {
    if (isConfirmDisabled) {
      return;
    }

    setSaving(true);
    try {
      const data = {
        CurrentStage: currentStage,
        CurrentStatus: currentStatus,
        UserId: Number(localStorage.getItem('userId')),
        ReasonStatusChange: detail.trim(),
        NewStatus: {
          StageId: StageIds.ANALYSIS,
          StatusId: Number(selectedStatusId),
        },
      };

      // Usamos el cliente directo (no el wrapper de servicio) para que un rechazo
      // del backend (transición inválida / concurrencia) llegue como excepción y
      // podamos mostrar el mensaje. La sync de etapa a CPE no aplica a subestados
      // de Análisis, por lo que no se pierde nada respecto al flujo del servicio.
      await new ClaimsTrackerClient().putClaimStatus(data, claimUId);

      setOpen(false);
      setFeedback({
        open: true,
        message: 'Subestado actualizado correctamente.',
        severity: 'success',
      });

      onStatusChanged?.();
    } catch (error) {
      // El backend rechaza transiciones inválidas o fuera de la matriz (incluye
      // el caso de concurrencia: otro analista ya cambió el subestado). El modal
      // permanece abierto con la selección para que el analista reintente, y se
      // refresca el estado real para que vea el subestado vigente.
      const axiosError = error as AxiosError;
      const problem = axiosError?.response?.data as
        | { detail?: string; title?: string }
        | undefined;
      const description =
        problem?.detail ??
        problem?.title ??
        'No se pudo actualizar el subestado. Intente nuevamente.';

      console.error('Error updating analysis substate:', error);
      setFeedback({ open: true, message: description, severity: 'error' });
      onStatusChanged?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    // Fragment (no Box) para que el botón sea hermano directo de los demás de
    // la barra de acciones y se alinee igual. El Dialog y el Snackbar se montan
    // por portal, así que no afectan el flujo de la fila.
    <>
      <Button
        className="update-analysis__trigger"
        text="Actualizar análisis"
        type="button"
        color="primary"
        // Mismo estilo que los demás botones de la barra (customStyle en
        // sections) para igualar altura y padding, y quedar alineado.
        style={{ padding: '8px 16px', height: 'auto' }}
        onClick={handleOpen}
      />

      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        classes={{ paper: 'update-analysis__dialog' }}
      >
        <DialogTitle className="update-analysis__dialog-title">
          Actualizar análisis
        </DialogTitle>
        <DialogContent className="update-analysis__dialog-content">
          <p className="update-analysis__question">
            ¿A qué subestado desea mover el análisis?
          </p>
          {loadingOptions ? (
            <div className="update-analysis__loading">
              <CircularProgress size={24} />
            </div>
          ) : optionsError ? (
            <Alert severity="error">
              No se pudieron cargar los subestados disponibles.
            </Alert>
          ) : (
            <>
              <FormControl
                fullWidth
                size="small"
                sx={{ mt: 1, backgroundColor: '#ffffff' }}
              >
                <InputLabel id="update-analysis-target">Subestado</InputLabel>
                <Select
                  labelId="update-analysis-target"
                  label="Subestado"
                  value={selectedStatusId === '' ? '' : selectedStatusId}
                  onChange={(event) =>
                    setSelectedStatusId(Number(event.target.value))
                  }
                >
                  {transitions.map((option) => (
                    <MenuItem key={option.statusId} value={option.statusId}>
                      {option.statusTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                className="update-analysis__detail"
                label="Detalle (opcional)"
                value={detail}
                onChange={handleDetailChange}
                multiline
                fullWidth
                size="small"
                sx={{ mt: 1.5 }}
                inputProps={{ maxLength: MAX_DETAIL_LENGTH }}
                helperText={`${detail.length}/${MAX_DETAIL_LENGTH}`}
              />
            </>
          )}
        </DialogContent>
        <DialogActions className="update-analysis__dialog-actions">
          <Button
            text="Cancelar"
            type="button"
            color="secondary"
            variant="outlined"
            size="small"
            disabled={saving}
            onClick={handleClose}
          />
          <Button
            text={saving ? 'Guardando...' : 'Aceptar'}
            type="button"
            color="primary"
            size="small"
            disabled={isConfirmDisabled}
            onClick={handleConfirm}
          />
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setFeedback((prev) => ({ ...prev, open: false }))}
          severity={feedback.severity}
          sx={{ width: '100%' }}
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default UpdateAnalysis;
